// Mock data for CRN Long-Form Sales Page

export const contactInfo = {
  phone: '587 288 7639',
  email: 'contact@crnnetwork.com',
  address: '',
  socials: []
};

export const images = {
  hero: 'https://images.unsplash.com/photo-1758691461957-474a7686e388?w=1200&q=80',
  problem: 'https://images.unsplash.com/photo-1584515933487-779824d29309?w=1200&q=80',
  solution: 'https://images.unsplash.com/photo-1666886573452-9dc8ce8f5cc5?w=800&q=80',
  team: 'https://images.pexels.com/photos/4266942/pexels-photo-4266942.jpeg?auto=compress&w=800',
  testimonials: 'https://images.unsplash.com/photo-1758691462126-2ee47c8bf9e7?w=1200&q=80',
  transformation: 'https://images.unsplash.com/photo-1631815588090-d4bfec5b1ccb?w=600&q=80'
};

export const heroContent = {
  preheading: 'For Recovery Program Directors & Treatment Center Owners',
  heading: 'Your Clients Are Fighting an Invisible Battle at the Cellular Level',
  subheading: 'But There Is a Breakthrough Solution That Changes Everything',
  description: 'Discover how leading recovery facilities are using science-backed cellular restoration to dramatically improve client outcomes, engagement, and long-term sobriety rates.',
  cta: 'Schedule Your Free Facility Assessment'
};

export const problemSection = {
  heading: 'The Hidden Problem Undermining Every Recovery Program',
  subheading: 'Years of Substance Use Leaves the Body Overloaded with Oxidative Stress',
  description: 'While you focus on the psychological and emotional aspects of recovery, there is a hidden physiological crisis happening at the cellular level that most treatment programs are not addressing.',
  intro: 'This hidden cellular damage is not just a minor issue. It is actively working against your treatment protocols, making recovery harder, longer, and less likely to succeed.',
  problems: [
    {
      title: 'Accelerated Aging',
      description: 'Cellular damage makes clients look and feel years older than they are, affecting self-esteem and motivation.',
      image: 'https://images.unsplash.com/photo-1620790647593-b3a6916c7d60?w=600&q=80'
    },
    {
      title: 'Chronic Inflammation',
      description: 'Persistent inflammation causes pain, discomfort, and makes it nearly impossible for the body to heal properly.',
      image: 'https://images.pexels.com/photos/897817/pexels-photo-897817.jpeg?auto=compress&w=600'
    },
    {
      title: 'Low Energy and Fatigue',
      description: 'Clients struggle to stay engaged in therapy and activities because their cells cannot produce adequate energy.',
      image: 'https://images.unsplash.com/photo-1607512774067-c0adbb97a205?w=600&q=80'
    },
    {
      title: 'Neurological Strain, Depression, Anxiety',
      description: 'Brain cells damaged by oxidative stress contribute to mood disorders that medication alone cannot fully address.',
      image: 'https://images.unsplash.com/photo-1633144588631-959c772e2ecd?w=600&q=80'
    },
    {
      title: 'Slower Emotional Healing',
      description: 'When the body is in crisis, emotional and psychological healing takes longer and is less effective.',
      image: 'https://images.unsplash.com/photo-1595178156906-2396ef837b0f?w=600&q=80'
    },
    {
      title: 'Future Oxidative Related Illness',
      description: 'Unaddressed cellular damage sets clients up for chronic diseases and health problems long after they leave your care, including weakened immunity leading to frequent illness and compromised overall health.',
      image: 'https://images.unsplash.com/photo-1758691463084-17ed846d4a50?w=600&q=80'
    }
  ],
  impact: {
    heading: 'The Real Cost to Your Facility',
    points: [
      'Lower completion rates as clients struggle with physical symptoms',
      'Decreased engagement in therapy and programming',
      'Higher relapse rates due to persistent low energy and depression',
      'More medical complications during treatment',
      'Difficulty demonstrating measurable outcomes to referral sources',
      'Competitive disadvantage as other facilities adopt cellular restoration'
    ]
  }
};

export const solutionSection = {
  heading: 'The Solution: Electrolyzed Reduced Hydrogen Water (ERW)',
  subheading: 'A Highly Antioxidant, Ultra-Hydrating Solution That Works at the Cellular Level',
  intro: 'For the past 7 years, we have worked with clients of various backgrounds across North America and Latin America, implementing a simple, science-backed solution that addresses oxidative stress directly.',
  whatIsERW: {
    heading: 'What is ERW?',
    description: 'Electrolyzed Reduced Hydrogen Water is not regular water. It is a powerful antioxidant solution that neutralizes free radicals, reduces inflammation, and supports the body natural healing systems at the cellular level.',
    tagline: 'It is simple, safe, and clinically supportive.'
  },
  benefits: [
    {
      icon: 'Shield',
      title: 'Neutralize Oxidative Stress',
      description: 'ERW directly targets and neutralizes the free radicals causing cellular damage, giving the body a clean slate to heal.'
    },
    {
      icon: 'Activity',
      title: 'Support Detox Pathways',
      description: 'Enhanced hydration supports the liver, kidneys, and lymphatic system in efficiently removing toxins from the body.'
    },
    {
      icon: 'Battery',
      title: 'Stabilize Energy Levels',
      description: 'Clients experience fewer energy crashes and more consistent vitality throughout their day.'
    },
    {
      icon: 'Brain',
      title: 'Improve Clarity and Mood',
      description: 'As brain cells heal from oxidative damage, clients report better focus, clearer thinking, and improved emotional stability.'
    },
    {
      icon: 'Heart',
      title: 'Reduce Inflammation Markers',
      description: 'Clinical studies show significant reductions in inflammation, leading to less pain and faster healing.'
    }
  ]
};

export const transformationSection = {
  heading: 'When Clients Feel Better Physically, Everything Changes',
  subheading: 'They Participate More Fully in Treatment',
  benefits: [
    {
      title: 'Faster Physical Recovery',
      description: 'Clients bounce back quicker from the physical toll of substance use, allowing them to engage in treatment sooner.'
    },
    {
      title: 'Fewer Low-Energy Crash Days',
      description: 'Stable energy means clients show up to therapy, activities, and programming ready to participate.'
    },
    {
      title: 'Better Sleep and Digestion',
      description: 'As the body heals at the cellular level, sleep quality improves dramatically and digestive issues resolve.'
    },
    {
      title: 'Improved Emotional Stability',
      description: 'With physical wellness supporting mental health, clients experience fewer mood swings and emotional crises.'
    },
    {
      title: 'Higher Engagement in Therapy',
      description: 'Clients have the physical and mental energy to actively participate in individual and group therapy sessions.'
    },
    {
      title: 'Stronger Long-Term Sobriety Outcomes',
      description: 'By addressing the cellular foundation of health, clients leave your program truly prepared for lasting recovery.'
    }
  ]
};

export const aboutSection = {
  heading: 'About The Cellular Recovery Network',
  tagline: 'Healing Starts Within',
  mission: 'We are a network of health and hydration professionals operating across North America and Latin America, dedicated to advancing recovery through cellular restoration.',
  experience: 'For the past 7 years, we have focused on the science and practical application of electrolysed reduced hydrogen water as a tool to neutralize oxidative stress and support the body natural healing systems.',
  philosophy: 'Our work centers on one mission: helping recovery programs address the free radical damage left behind by long-term substance use. By supporting patients at the cellular level, we empower treatment centers to improve physical resilience, emotional stability, and long-term sobriety outcomes.',
  belief: 'We believe recovery should restore more than habits, trauma and mindset. It should restore the cells themselves.',
  commitment: 'We are here to help you and your clients start stronger and deeper healing with a focus on their future health and longevity.'
};

export const implementationSection = {
  heading: 'Implementing CRN at Your Facility is Effortless',
  subheading: 'No Disruption. No Training. Just Results.',
  benefits: [
    {
      icon: 'UserX',
      title: 'No Staff Training Required',
      description: 'Your team can focus on what they do best. Our system is completely intuitive and requires zero training.'
    },
    {
      icon: 'Plug',
      title: 'No Major Installation - Plug and Pour',
      description: 'Simply plug in the hydration station and start serving. No plumbing modifications or facility changes needed.'
    },
    {
      icon: 'Droplets',
      title: '9 pH Balanced Waters',
      description: 'Multiple pH options ensure optimal hydration for different needs throughout the recovery process.'
    },
    {
      icon: 'Users',
      title: 'Great for the Staff Too',
      description: 'Your team will also benefit from access to hydrogen-rich water, supporting their health and energy levels.'
    },
    {
      icon: 'TrendingUp',
      title: 'Added Value and Impact for Clients',
      description: 'Demonstrate your commitment to comprehensive, cutting-edge care that goes beyond traditional treatment.'
    },
    {
      icon: 'Award',
      title: 'Competitive Advantage',
      description: 'Add Cellular Recovery Support to your program, giving you a unique selling point in the recovery industry.'
    }
  ],
  additionalServices: {
    heading: 'Additional Free Services Included',
    services: [
      'Collaboration opportunities with other facilities in our network',
      'Dedicated customer service, technical support, and ongoing consultation on program optimization',
      'Comprehensive educational materials for staff and clients'
    ]
  }
};

export const benefitsGrid = [
  {
    id: 1,
    icon: 'Droplet',
    title: 'Hydration Station',
    description: 'Hydrogen-rich antioxidant water available on-site for clients throughout their stay.',
    color: 'teal'
  },
  {
    id: 2,
    icon: 'GraduationCap',
    title: 'Education & Training',
    description: 'Comprehensive client and staff education on hydration science and cellular recovery principles.',
    color: 'blue'
  },
  {
    id: 3,
    icon: 'Sparkles',
    title: 'Non Toxic Cleaning',
    description: 'Remove harmful oxidative cleaners from your facility, kitchen, and food handling areas.',
    color: 'green'
  },
  {
    id: 4,
    icon: 'HeartPulse',
    title: 'Topical Medical Treatment',
    description: 'Learn how HoCL can be used to treat ulcers, infections and other skin issues naturally.',
    color: 'amber'
  },
  {
    id: 5,
    icon: 'Beaker',
    title: 'Non Toxic Cleaners Made On Demand',
    description: 'Create safe, effective cleaning solutions on-site that protect clients and staff from harsh chemicals.',
    color: 'teal'
  },
  {
    id: 6,
    icon: 'Apple',
    title: 'Food Prep with Anti Bacterial Wash',
    description: 'Pesticide removal and antibacterial solutions for safer food preparation throughout your facility.',
    color: 'green'
  }
];

export const testimonials = [
  {
    id: 1,
    name: 'Jon',
    role: 'Recovery Client',
    quote: 'Within my first week of drinking ERW hydrogen water, my energy lifted, my mindset shifted, and my entire recovery process elevated. It genuinely changed my life.',
    rating: 5
  },
  {
    id: 2,
    name: 'Barb',
    role: 'Recovery Client',
    quote: 'Hydrogen water gave me energy and clarity I had not felt in years. It made recovery feel possible again.',
    rating: 5
  }
];

export const faqs = [
  {
    id: 1,
    question: 'What is Electrolyzed Reduced Hydrogen Water (ERW)?',
    answer: 'ERW is a highly antioxidant, ultra-hydrating water produced through electrolysis. It helps neutralize oxidative stress, support detox pathways, and promote cellular healing—making it ideal for recovery programs. Unlike regular water, ERW contains dissolved molecular hydrogen that acts as a powerful antioxidant at the cellular level.'
  },
  {
    id: 2,
    question: 'How quickly will we see results with our clients?',
    answer: 'Many facilities report noticeable improvements in client energy levels and engagement within the first 2-4 weeks. Physical and emotional benefits typically become more pronounced over the first 90 days of consistent use. Some clients report feeling different within the first week.'
  },
  {
    id: 3,
    question: 'Is installation complicated or disruptive?',
    answer: 'Not at all! Our hydration stations are designed to be Plug and Pour. No major installation, no plumbing modifications, and no staff training is required. We provide full technical support and educational materials. You can be up and running the same day.'
  },
  {
    id: 4,
    question: 'What ongoing support do you provide?',
    answer: 'We offer comprehensive customer service, technical support, educational materials for both staff and clients, and collaboration opportunities with other facilities in our network. You are never alone in this journey. Our team is available to help optimize your program and answer any questions.'
  },
  {
    id: 5,
    question: 'Is ERW safe for all clients?',
    answer: 'Yes! ERW is simple, safe, and clinically supportive. It is essentially ultra-purified water with added hydrogen molecules. There are no side effects or contraindications. However, we always recommend consulting with your medical team about specific client needs and conditions.'
  },
  {
    id: 6,
    question: 'How does this give our program a competitive advantage?',
    answer: 'By adding Cellular Recovery Support to your program, you are offering something most facilities do not have—a science-backed approach to addressing the physical damage of substance use at the cellular level. This differentiates your program, demonstrates your commitment to comprehensive care, and gives referral sources and families a compelling reason to choose your facility.'
  },
  {
    id: 7,
    question: 'What is the investment required?',
    answer: 'We understand every facility has unique needs and budgets. During your free consultation, we will discuss pricing based on your facility size and requirements. Most programs find the investment pays for itself through improved outcomes, higher completion rates, and enhanced reputation. We offer flexible options to fit your budget.'
  }
];

export const ctaContent = {
  heading: 'Ready to Give Your Clients the Cellular Foundation for Lasting Recovery?',
  subheading: 'Schedule Your Free Facility Assessment Today',
  description: 'Discover exactly how The Cellular Restoration Network can enhance your program, improve outcomes, and give you a competitive advantage in the recovery industry.',
  benefits: [
    'Free consultation with our cellular restoration specialists',
    'Custom implementation plan for your facility',
    'Educational resources for your staff and clients',
    'No long-term contracts or obligations'
  ],
  urgency: 'Facilities that implement cellular restoration see measurable improvements in client engagement within 30 days. The sooner you start, the sooner your clients benefit.'
};
